import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, TextField,
  List, ListItemButton, ListItemText, ListSubheader, Typography, FormControlLabel, Switch,
} from '@mui/material';
import { deptLabel } from './dispatchConstants';

const TargetPickerDialog = ({
  open, onClose, departments, scope, settingOptions, initialTab, onSelect,
}) => {
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  const [includeGroup, setIncludeGroup] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(initialTab === 'setting' ? 1 : 0);
      setQuery('');
      setIncludeGroup(false);
    }
  }, [open, initialTab]);

  const { storeDepts, groupDepts } = useMemo(() => {
    if (!scope) return { storeDepts: departments, groupDepts: [] };
    return {
      storeDepts: departments.filter(d => d.dealerships.length === 0 || d.dealerships.includes(scope)),
      groupDepts: departments.filter(d => d.dealerships.length > 0 && !d.dealerships.includes(scope)),
    };
  }, [departments, scope]);

  const settingValueLabel = (v) => {
    if (v == null || v === '') return 'no value set';
    const d = departments.find(o => String(o.id) === String(v));
    return d ? deptLabel(d) : String(v);
  };

  const q = query.trim().toLowerCase();
  const matchDept = d => !q || String(d.name).toLowerCase().includes(q) || String(d.name_alias).toLowerCase().includes(q);
  const storeFiltered = storeDepts.filter(matchDept);
  const groupFiltered = groupDepts.filter(matchDept);
  const settingFiltered = settingOptions.filter(o => !q || o.key.toLowerCase().includes(q));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose target</DialogTitle>
      <Tabs value={tab} onChange={(e, v) => { setTab(v); setQuery(''); }} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Departments" />
        <Tab label={`Settings (${settingOptions.length})`} />
      </Tabs>
      <DialogContent dividers>
        <TextField size="small" fullWidth placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} sx={{ mb: 1 }} />
        {tab === 0 ? (
          <>
            {scope && (
              <FormControlLabel
                control={<Switch size="small" checked={includeGroup} onChange={e => setIncludeGroup(e.target.checked)} />}
                label="Include group departments (other locations)"
              />
            )}
            <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
              {storeFiltered.length === 0 && !includeGroup && (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>No departments for this location.</Typography>
              )}
              {storeFiltered.map(d => (
                <ListItemButton key={d.id} onClick={() => onSelect({ type: 'department', id: d.id, name: d.name })}>
                  <ListItemText primary={deptLabel(d)} />
                </ListItemButton>
              ))}
              {includeGroup && (
                <ListSubheader disableSticky>Group departments</ListSubheader>
              )}
              {includeGroup && groupFiltered.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>No departments from other locations.</Typography>
              )}
              {includeGroup && groupFiltered.map(d => (
                <ListItemButton key={d.id} onClick={() => onSelect({ type: 'department', id: d.id, name: d.name })}>
                  <ListItemText
                    primary={deptLabel(d)}
                    primaryTypographyProps={{ fontWeight: 700 }}
                    secondary={d.dealerships.length ? d.dealerships.join(', ') : 'all locations'}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {settingFiltered.length === 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>No setting keys found for this location.</Typography>
            )}
            {settingFiltered.map(o => (
              <ListItemButton key={o.key} onClick={() => onSelect({ type: 'setting', key: o.key })}>
                <ListItemText primary={o.key} secondary={`→ ${settingValueLabel(o.value)}`} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TargetPickerDialog;
